import { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { usePrintEditorStore } from '../state/usePrintEditorStore';
import { parseColor, rgbaToHex, rgbToHsv, hsvToRgb, hexNoAlpha, alphaPercent } from './colorUtils';
import { T } from './figma';

const SWATCHES = [
    '#000000', '#545454', '#737373', '#a6a6a6', '#d9d9d9', '#ffffff',
    '#f24822', '#ff824d', '#ffa629', '#ffcd29', '#14ae5c', '#0d99ff',
    '#9747ff', '#f24e9d', '#e68a00', '#18181b', '#3f3f46', '#e4e4e7',
];

const checker = {
    backgroundImage:
        'linear-gradient(45deg, #d4d4d8 25%, transparent 25%, transparent 75%, #d4d4d8 75%), linear-gradient(45deg, #d4d4d8 25%, transparent 25%, transparent 75%, #d4d4d8 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 4px 4px',
    backgroundColor: '#ffffff',
};

function gesture(fn) {
    try { fn(usePrintEditorStore.getState()); } catch { /* store optional in tests */ }
}

/**
 * Text draft for a field whose value can ALSO move while it holds focus — dragging
 * the picker surface, clicking a swatch, an edit from another panel. Without this
 * the draft wins on blur and silently overwrites the freshly picked colour with
 * whatever string was in the box when it was focused. Same guard NumInput uses.
 */
function useLiveDraft(value) {
    const [draft, setDraft] = useState(null);
    const baseRef = useRef(value);
    useEffect(() => {
        if (draft != null && value !== baseRef.current) setDraft(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- follow the live value only
    }, [value]);
    /** Call from onFocus: snapshot what this edit started from. */
    const beginDraft = (text) => { baseRef.current = value; setDraft(text); };
    return { draft, setDraft, beginDraft };
}

/** Blur-commit helper: ignore an untouched field so it can't add a no-op undo step. */
const sameHex = (a, b) => String(a).replace(/^#/, '').toUpperCase() === String(b).replace(/^#/, '').toUpperCase();

/** Drag surface helper — pointer position as 0..1 fractions */
function useDrag2D(onMove) {
    const ref = useRef(null);
    const onPointerDown = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        e.preventDefault();
        gesture((s) => s.beginHistoryGesture());
        const apply = (ev) => {
            const rect = el.getBoundingClientRect();
            const x = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
            const y = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
            onMove(x, y);
        };
        apply(e);
        const move = (ev) => apply(ev);
        const up = () => {
            gesture((s) => s.endHistoryGesture());
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    }, [onMove]);
    return { ref, onPointerDown };
}

/**
 * Figma-style color picker surface (SV square, hue + alpha sliders,
 * hex / alpha inputs, swatch grid, optional transparent action).
 */
export function ColorPicker({ value, onChange, allowEmpty = false }) {
    const rgba = parseColor(value) || { r: 0, g: 0, b: 0, a: 1 };
    // Keep hue/sat stable while dragging through grays — HSV kept in state,
    // re-synced only when the external value stops matching the last emit.
    const [hsva, setHsva] = useState(() => ({ ...rgbToHsv(rgba), a: rgba.a }));
    const lastEmit = useRef(value);

    useEffect(() => {
        if (lastEmit.current === value) return;
        const next = parseColor(value);
        if (next) setHsva({ ...rgbToHsv(next), a: next.a });
        lastEmit.current = value;
    }, [value]);

    const emit = (next) => {
        setHsva(next);
        const hex = rgbaToHex({ ...hsvToRgb(next), a: next.a });
        lastEmit.current = hex;
        onChange(hex);
    };

    const sv = useDrag2D((x, y) => emit({ ...hsva, s: x, v: 1 - y }));
    const hue = useDrag2D((x) => emit({ ...hsva, h: x * 360 }));
    const alpha = useDrag2D((x) => emit({ ...hsva, a: Math.round(x * 100) / 100 }));

    const pureHue = hsvToRgb({ h: hsva.h, s: 1, v: 1 });
    const cur = hsvToRgb(hsva);
    const curHex = rgbaToHex({ ...cur, a: 1 });
    const isTransparent = value === 'transparent' || (!value && allowEmpty);

    const pickEyedropper = async () => {
        if (typeof window === 'undefined' || !window.EyeDropper) return;
        try {
            const res = await new window.EyeDropper().open();
            if (res?.sRGBHex) {
                const next = parseColor(res.sRGBHex);
                if (next) emit({ ...rgbToHsv(next), a: hsva.a });
            }
        } catch { /* cancelled */ }
    };

    return (
        <div className="w-[232px] select-none">
            {/* SV square */}
            <div
                ref={sv.ref}
                onPointerDown={sv.onPointerDown}
                className="relative w-full h-[160px] rounded-[6px] cursor-crosshair touch-none"
                style={{
                    backgroundColor: `rgb(${pureHue.r},${pureHue.g},${pureHue.b})`,
                    backgroundImage:
                        'linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, rgba(255,255,255,0))',
                }}
            >
                <div
                    className="absolute w-3 h-3 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    style={{
                        left: `${hsva.s * 100}%`,
                        top: `${(1 - hsva.v) * 100}%`,
                        backgroundColor: `rgb(${cur.r},${cur.g},${cur.b})`,
                    }}
                />
            </div>

            <div className="flex items-center gap-2 mt-3">
                <button
                    type="button"
                    title="Pick color from screen"
                    onClick={pickEyedropper}
                    className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-[5px] ${T.hoverBg} text-black/70 dark:text-white/70 ${typeof window !== 'undefined' && window.EyeDropper ? '' : 'hidden'}`}
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
                        <path d="M10.5 6.5L3.7 13.3a1 1 0 0 1-.7.3H1.6a.2.2 0 0 1-.2-.2v-1.4a1 1 0 0 1 .3-.7l6.8-6.8M10.5 6.5l1.2 1.2M10.5 6.5 9.3 5.3m2.4 2.4 1.9-1.9a1.7 1.7 0 1 0-2.4-2.4L9.3 5.3" />
                    </svg>
                </button>
                <div className="flex-1 flex flex-col gap-2">
                    {/* Hue */}
                    <div
                        ref={hue.ref}
                        onPointerDown={hue.onPointerDown}
                        className="relative h-3 rounded-full cursor-pointer touch-none"
                        style={{
                            background:
                                'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
                        }}
                    >
                        <div
                            className="absolute top-1/2 w-3 h-3 rounded-full bg-white border border-black/20 shadow -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                            style={{ left: `${(hsva.h / 360) * 100}%` }}
                        />
                    </div>
                    {/* Alpha */}
                    <div
                        ref={alpha.ref}
                        onPointerDown={alpha.onPointerDown}
                        className="relative h-3 rounded-full cursor-pointer overflow-hidden touch-none"
                        style={checker}
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                background: `linear-gradient(to right, rgba(${cur.r},${cur.g},${cur.b},0), rgb(${cur.r},${cur.g},${cur.b}))`,
                            }}
                        />
                        <div
                            className="absolute top-1/2 w-3 h-3 rounded-full bg-white border border-black/20 shadow -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                            style={{ left: `${hsva.a * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Hex + alpha inputs */}
            <div className="flex items-center gap-1.5 mt-3">
                <span className={`text-[11px] ${T.textSoft} pl-0.5 pr-1`}>Hex</span>
                <HexInput hex={isTransparent ? '' : curHex} onCommit={(h) => {
                    const next = parseColor(h);
                    if (next) emit({ ...rgbToHsv(next), a: hsva.a });
                }} />
                <AlphaInput
                    value={Math.round(hsva.a * 100)}
                    onCommit={(p) => emit({ ...hsva, a: Math.min(100, Math.max(0, p)) / 100 })}
                />
            </div>

            {/* Swatches */}
            <div className={`mt-3 pt-3 border-t ${T.border}`}>
                <div className="grid grid-cols-9 gap-1.5">
                    {allowEmpty && (
                        <button
                            type="button"
                            title="Transparent"
                            onClick={() => { lastEmit.current = 'transparent'; onChange('transparent'); setHsva((p) => ({ ...p, a: 0 })); }}
                            className={`relative h-5 w-5 rounded-[4px] border overflow-hidden ${isTransparent ? 'ring-2 ring-[#0d99ff] border-transparent' : 'border-black/10 dark:border-white/20'}`}
                            style={checker}
                        >
                            <span className="absolute inset-0" style={{
                                backgroundImage: 'linear-gradient(to top right, transparent calc(50% - 1px), #f24822 calc(50% - 1px), #f24822 calc(50% + 1px), transparent calc(50% + 1px))',
                            }} />
                        </button>
                    )}
                    {SWATCHES.slice(0, allowEmpty ? 17 : 18).map((c) => (
                        <button
                            key={c}
                            type="button"
                            title={c}
                            onClick={() => {
                                const next = parseColor(c);
                                if (next) emit({ ...rgbToHsv(next), a: 1 });
                            }}
                            className={`h-5 w-5 rounded-[4px] border ${String(value).toLowerCase() === c ? 'ring-2 ring-[#0d99ff] border-transparent' : 'border-black/10 dark:border-white/20'}`}
                            style={{ backgroundColor: c }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function HexInput({ hex, onCommit }) {
    const live = hex.replace(/^#/, '').toUpperCase();
    const { draft, setDraft, beginDraft } = useLiveDraft(live);
    const shown = draft != null ? draft : live;
    return (
        <input
            type="text"
            spellCheck={false}
            className={`flex-1 min-w-0 h-7 px-2 text-[11px] uppercase rounded-[5px] border border-transparent outline-none
                bg-[#f5f5f5] dark:bg-[#383838] ${T.text}
                hover:border-[#e6e6e6] dark:hover:border-[#555555] focus:!border-[#0d99ff] focus:ring-1 focus:ring-[#0d99ff]`}
            value={shown}
            placeholder="FFFFFF"
            onFocus={(e) => { beginDraft(live); requestAnimationFrame(() => e.target.select?.()); }}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onBlur={(e) => {
                const v = e.target.value.trim();
                setDraft(null);
                if (sameHex(v, live)) return;
                if (/^#?[0-9a-f]{3,8}$/i.test(v)) onCommit(v.startsWith('#') ? v : `#${v}`);
            }}
        />
    );
}

function AlphaInput({ value, onCommit }) {
    const { draft, setDraft, beginDraft } = useLiveDraft(value);
    return (
        <input
            type="text"
            inputMode="numeric"
            className={`w-12 shrink-0 h-7 px-2 text-[11px] rounded-[5px] border border-transparent outline-none
                bg-[#f5f5f5] dark:bg-[#383838] ${T.text}
                hover:border-[#e6e6e6] dark:hover:border-[#555555] focus:!border-[#0d99ff] focus:ring-1 focus:ring-[#0d99ff]`}
            value={draft != null ? draft : `${value}%`}
            onFocus={(e) => { beginDraft(String(value)); requestAnimationFrame(() => e.target.select?.()); }}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onBlur={(e) => {
                const p = parseInt(e.target.value, 10);
                setDraft(null);
                if (Number.isFinite(p) && p !== value) onCommit(p);
            }}
        />
    );
}

/**
 * Property-panel color row: [swatch → picker popover] [hex] [alpha %]
 * Matches Figma's Fill / Stroke row anatomy.
 */
export function ColorRow({ value, onChange, allowEmpty = false, disabled = false, trailing = null, label = null }) {
    const isTransparent = value === 'transparent' || (!value && allowEmpty);
    const rgba = parseColor(value);

    const setHex = (h) => {
        const next = parseColor(h);
        if (!next) return;
        // Preserve current alpha when typing a 6-digit hex
        const a = rgba?.a ?? 1;
        onChange(rgbaToHex({ ...next, a: next.a < 1 ? next.a : a }));
    };

    const setAlpha = (p) => {
        const base = rgba || { r: 0, g: 0, b: 0, a: 1 };
        onChange(rgbaToHex({ ...base, a: Math.min(100, Math.max(0, p)) / 100 }));
    };

    return (
        <div className={`flex items-center gap-1 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
            <div className={`flex items-center flex-1 min-w-0 h-7 rounded-[5px] border border-transparent ${T.inputBg}
                hover:border-[#e6e6e6] dark:hover:border-[#555555] focus-within:!border-[#0d99ff] focus-within:ring-1 focus-within:ring-[#0d99ff] transition-colors`}
            >
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            title="Change color"
                            className="ml-1.5 mr-1 h-4 w-4 shrink-0 rounded-[3px] border border-black/10 dark:border-white/20 overflow-hidden relative"
                            style={isTransparent ? checker : undefined}
                        >
                            {!isTransparent && (
                                <span className="absolute inset-0" style={{ backgroundColor: value ? rgbaToHex({ ...(rgba || { r: 0, g: 0, b: 0 }), a: 1 }) : '#000000' }} />
                            )}
                            {isTransparent && (
                                <span className="absolute inset-0" style={{
                                    backgroundImage: 'linear-gradient(to top right, transparent calc(50% - 1px), #f24822 calc(50% - 1px), #f24822 calc(50% + 1px), transparent calc(50% + 1px))',
                                }} />
                            )}
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        side="left"
                        align="start"
                        sideOffset={12}
                        // Radix focuses the first tabbable child on open — here the Hex
                        // box, which then sits selected while you drag the picker and
                        // blur-commits its stale text over the colour you just chose.
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        className="w-auto p-3 rounded-[13px] border-[#e6e6e6] dark:border-[#444444] dark:bg-[#2c2c2c] shadow-xl"
                    >
                        {label && <div className={`text-[11px] font-semibold mb-2 ${T.text}`}>{label}</div>}
                        <ColorPicker value={value} onChange={onChange} allowEmpty={allowEmpty} />
                    </PopoverContent>
                </Popover>
                <HexBare value={isTransparent ? '' : hexNoAlpha(value)} placeholder={isTransparent ? 'Transparent' : 'FFFFFF'} onCommit={setHex} />
                <span className="w-px self-stretch my-1.5 bg-black/5 dark:bg-white/10" />
                <AlphaBare value={isTransparent ? 0 : alphaPercent(value)} onCommit={setAlpha} />
            </div>
            {trailing}
        </div>
    );
}

function HexBare({ value, onCommit, placeholder }) {
    const { draft, setDraft, beginDraft } = useLiveDraft(value);
    return (
        <input
            type="text"
            spellCheck={false}
            className={`flex-1 min-w-0 h-full bg-transparent outline-none text-[11px] uppercase ${T.text} placeholder:normal-case placeholder:text-black/30 dark:placeholder:text-white/30`}
            value={draft != null ? draft : value}
            placeholder={placeholder}
            onFocus={(e) => { beginDraft(value); requestAnimationFrame(() => e.target.select?.()); }}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onBlur={(e) => {
                const v = e.target.value.trim();
                setDraft(null);
                if (sameHex(v, value)) return;
                if (/^#?[0-9a-f]{3,8}$/i.test(v)) onCommit(v.startsWith('#') ? v : `#${v}`);
            }}
        />
    );
}

function AlphaBare({ value, onCommit }) {
    const { draft, setDraft, beginDraft } = useLiveDraft(value);
    return (
        <input
            type="text"
            inputMode="numeric"
            className={`w-11 shrink-0 h-full px-1.5 bg-transparent outline-none text-[11px] ${T.text}`}
            value={draft != null ? draft : `${value}%`}
            title="Opacity"
            onFocus={(e) => { beginDraft(String(value)); requestAnimationFrame(() => e.target.select?.()); }}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onBlur={(e) => {
                const p = parseInt(e.target.value, 10);
                setDraft(null);
                if (Number.isFinite(p) && p !== value) onCommit(p);
            }}
        />
    );
}
